import React, { useCallback, useMemo } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";

import classnames from "classnames";
import { closeMenu, getLoggedIn, isGuestAccountActive, Translation as T, useAmsSelector } from "@awe-web/shared/lib";
import { logoutAndRedirect } from "../user/actions.js";
import { B2C_SelectStationAndTime, B2B_SelectStationAndTime } from "../routing/routes.js";
import { getSignUpScreenLink, getLoginScreenPath } from "../user/store.js";
import { belongsToGroup, useBelongsToGroup } from "../routing/util.js";
import UserProfileLink from "./user_profile_link.jsx";
import { screenGroups } from "../routing/constants.js";

import "./user_navigation.scss";

const PRIVATE_CUSTOMERS = "ALG0000008";
const BUSINESS_CUSTOMERS = "ALG0000009";
const LOGOUT = "FSD0000258";
const GUEST_LOGOUT = "FSD0000456";
const LOGIN = "FSD0000141";

export default function UserNavigation({ className }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const loggedIn = useAmsSelector((state) => getLoggedIn(state));

  const checkLoginLocation = useCallback((screenGroup) => {
    return belongsToGroup(location, screenGroup);
  }, [location]);

  const loginScreenPath = getLoginScreenPath(checkLoginLocation);

  const checkSignUpLocation = useCallback((screenGroup) => belongsToGroup(location, screenGroup), [location]);
  const stateSignUpScreenLink = useAmsSelector((state) => getSignUpScreenLink(state)(checkSignUpLocation));
  const signUpScreenLink = useMemo(() => stateSignUpScreenLink.toJS(), [stateSignUpScreenLink]);

  const onLogout = useCallback(async () => {
    const redirectPath = await dispatch(logoutAndRedirect());
    navigate(redirectPath, { replace: true });
  }, [dispatch, navigate]);

  const combinedClassName = classnames("user-navigation", className);

  const isB2bScreen = useBelongsToGroup(screenGroups.B2B);
  const isB2cScreen = useBelongsToGroup(screenGroups.B2C);
  const isLoginScreen = useBelongsToGroup(screenGroups.LOGIN);
  const isProfileScreen = useBelongsToGroup(screenGroups.PROFILE);
  const isSignUpScreen = useBelongsToGroup(screenGroups.SIGN_UP);

  const isGuestAccount = useSelector(state => isGuestAccountActive(state));

  const computeLinkClass = useCallback((isActive) => {
    const result = ["user-navigation__link"];
    if (isActive) {
      result.push("user-navigation__link--active");
    }
    return result.join(' ');
  }, []);

  const computeLoginLinkClass = useCallback((isActive) => {
    const result = ["user-navigation__link", "user-navigation__link--login"];
    if (isActive) {
      result.push("user-navigation__link--login--active");
    }
    return result.join(' ');
  }, []);

  const onClickHandler = useCallback(() => {
    dispatch(closeMenu());
  }, [dispatch]);

  return (
    <div className={combinedClassName}>
      {loggedIn
        ? (
          <div className="user-navigation__row">
            <span className="user-navigation__item user-navigation__item--profile">
              <UserProfileLink showAsActive={isProfileScreen} />
            </span>
            <span className="user-navigation__item">
              <button className="user-navigation__link user-navigation__link--logout" onClick={onLogout}>
                <T code={ isGuestAccount ? GUEST_LOGOUT : LOGOUT } />
              </button>
            </span>
          </div>
        ) : (
          <>
            <div className="user-navigation__row">
              <span className="user-navigation__item">
                <NavLink
                  onClick={onClickHandler}
                  className={({ isActive }) => computeLinkClass(isActive || isB2cScreen)}
                  to={B2C_SelectStationAndTime.path()}
                >
                  <T code={PRIVATE_CUSTOMERS} />
                </NavLink>
              </span>

              <span className="user-navigation__item">
                <NavLink
                  onClick={onClickHandler}
                  className={({ isActive }) => computeLinkClass(isActive || isB2bScreen)}
                  to={B2B_SelectStationAndTime.path()}
                >
                  <T code={BUSINESS_CUSTOMERS} />
                </NavLink>
              </span>
            </div>
            <div className="user-navigation__row">
              <span className="user-navigation__item">
                <NavLink
                  onClick={onClickHandler}
                  className={({ isActive }) => computeLoginLinkClass(isActive || isLoginScreen)}
                  to={loginScreenPath}
                >
                  <T code={LOGIN} />
                </NavLink>
              </span>
              <span className="user-navigation__item">
                <NavLink
                  onClick={onClickHandler}
                  className={({ isActive }) => computeLinkClass(isActive || isSignUpScreen)}
                  to={signUpScreenLink.path}
                >
                  {signUpScreenLink.label}
                </NavLink>
              </span>
            </div>
          </>
        )}
    </div>
  );
}

UserNavigation.propTypes = {
  className: PropTypes.string
};
